// tenant/HomePages/TenantHomePage.tsx
import React, { useEffect, useState } from 'react';
import { useTenantUserInfo } from '../../context/userInfoProvider';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

const pageContainer: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  opacity: 0.8,
  marginTop: 4,
};

const sectionGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 16,
};

const sectionCard: React.CSSProperties = {
  backgroundColor: 'rgba(23, 8, 36, 0.9)',
  borderRadius: 12,
  padding: 16,
  border: '1px solid rgba(255,255,255,0.04)',
};

const sectionHeader: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const sectionLabelPill: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  padding: '3px 8px',
  borderRadius: 999,
  backgroundColor: '#312047',
  color: '#C7B6FF',
};

const pillButton: React.CSSProperties = {
  display: 'inline-block',
  padding: '8px 14px',
  borderRadius: 999,
  backgroundColor: '#7B1FA2',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  fontSize: 14,
};

const inlineMetaRow: React.CSSProperties = {
  fontSize: 14,
  marginTop: 4,
  opacity: 0.9,
};

const itemRowWrapper: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  marginTop: 8,
};

const itemCard: React.CSSProperties = {
  flex: '0 0 auto',
  minWidth: 160,
  maxWidth: 220,
  borderRadius: 10,
  padding: 10,
  background:
    'linear-gradient(145deg, rgba(74,47,120,0.9), rgba(49,25,86,0.9))',
  boxShadow: '0 8px 18px rgba(0,0,0,0.35)',
};

const smallLabel: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  opacity: 0.7,
  marginBottom: 2,
};

const smallValue: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
};

const emptyText: React.CSSProperties = {
  fontSize: 14,
  opacity: 0.8,
  marginTop: 4,
};

const badgeSoon: React.CSSProperties = {
  fontSize: 11,
  padding: '3px 6px',
  borderRadius: 999,
  border: '1px dashed rgba(255,255,255,0.25)',
  marginLeft: 8,
  opacity: 0.8,
};

const TenantHomePage: React.FC = () => {
  const {
    fetchData,
    loading,
    dataSourceNewOrders,
    dataSourceStorage,
    dataSourceHome,
  } = useTenantUserInfo();

  const [localLoading, setLocalLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLocalLoading(true);
      await fetchData();
      setLocalLoading(false);
    };
    load();
  }, [fetchData]);

  const upcomingOrder = dataSourceNewOrders && dataSourceNewOrders[0];

  const history = useHistory();

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        {/* Header */}
        <div style={headerRow}>
          <div>
            <h1 style={titleStyle}>Your storage at a glance</h1>
            <p style={subtitleStyle}>
              Track upcoming appointments and see what&apos;s in storage with
              Bins.
            </p>
          </div>
        </div>

        {/* Content */}
        {(localLoading || loading) ? (
          <div style={sectionCard}>
            <p>Loading your storage info…</p>
          </div>
        ) : (
          <div style={sectionGrid}>
            {/* Upcoming Order */}
            <section style={sectionCard}>
              <div style={sectionHeader}>
                <span>Upcoming order</span>
                <span style={sectionLabelPill}>Next appointment</span>
              </div>

              {!upcomingOrder ? (
                <>
                  <p style={emptyText}>
                    You don&apos;t have any upcoming pickups or deliveries yet.
                  </p>
                  <button
                    style={{ ...pillButton, marginTop: 8 }}
                    onClick={() => history.push(`/tenant/newappointment`)}> 
                    Schedule appointment
                  </button>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 15, marginBottom: 4 }}>
                    <strong>
                      {moment(upcomingOrder.date).format('MMMM DD, YYYY')}
                    </strong>{' '}
                    · {upcomingOrder.time}
                  </p>
                  <p style={inlineMetaRow}>
                    Status: <strong>{upcomingOrder.status}</strong>
                  </p>
                  {upcomingOrder.address && (
                    <p style={inlineMetaRow}>
                      Address:{' '}
                      <span style={{ opacity: 0.9 }}>
                        {typeof upcomingOrder.address === 'string'
                          ? upcomingOrder.address
                          : // handle object case
                            `${upcomingOrder.address.addressLine1 ?? ''}${
                              upcomingOrder.address.city
                                ? `, ${upcomingOrder.address.city}`
                                : ''
                            }`}
                      </span>
                    </p>
                  )}
                  <button
                    style={{ ...pillButton, marginTop: 10 }}
                    onClick={() => history.push(`/tenant/orders/${upcomingOrder.id}`)}
                  >
                    View &amp; update
                  </button>
                </>
              )}
            </section>

            {/* Items in Storage */}
            <section style={sectionCard}>
              <div style={sectionHeader}>
                <span>Items in storage</span>
                <span style={sectionLabelPill}>At facility</span>
              </div>

              {(!dataSourceStorage || dataSourceStorage.length === 0) ? (
                <p style={emptyText}>
                  Once we&apos;ve stored your items, you&apos;ll see them here.
                </p>
              ) : (
                <>
                  <div style={itemRowWrapper}>
                    {dataSourceStorage.slice(0, 2).map(item => (
                      <div key={item.id} style={itemCard}>
                        <div style={smallLabel}>Description</div>
                        <div style={smallValue}>
                          {item.description
                            ? String(item.description).slice(0, 30)
                            : 'Item'}
                        </div>
                        <div style={{ ...smallLabel, marginTop: 6 }}>ID</div>
                        <div style={smallValue}>#{String(item.id).slice(0, 10)}</div>
                      </div>
                    ))}
                  </div>
                  <button
                    style={{ ...pillButton, marginTop: 10 }}
                    onClick={() => history.push(`/tenant/home/storageinventory`)}
                  >
                    View all items in storage
                  </button>
                </>
              )}
            </section>

            {/* Returned Items */}
            <section style={sectionCard}>
              <div style={sectionHeader}>
                <span>Returned items</span>
                <span style={sectionLabelPill}>At home</span>
              </div>

              {(!dataSourceHome || dataSourceHome.length === 0) ? (
                <p style={emptyText}>
                  When we deliver items back to you, they&apos;ll appear here.
                </p>
              ) : (
                <>
                  <div style={itemRowWrapper}>
                    {dataSourceHome.slice(0, 2).map(item => (
                      <div key={item.id} style={itemCard}>
                        <div style={smallLabel}>Description</div>
                        <div style={smallValue}>
                          {item.description
                            ? String(item.description).slice(0, 30)
                            : 'Item'}
                        </div>
                        <div style={{ ...smallLabel, marginTop: 6 }}>ID</div>
                        <div style={smallValue}>#{String(item.id).slice(0, 10)}</div>
                      </div>
                    ))}
                  </div>
                  <button
                    style={{ ...pillButton, marginTop: 10 }}
                    onClick={() => history.push(`/tenant/home/homeinventory`)}
                  >
                    View all returned items
                  </button>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantHomePage;
